import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchable-select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../../components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { cn } from "../../components/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { motion, useAnimation } from "framer-motion";
import {
  Search,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { datePickerStyles } from "../../components/constants/styles";
import "react-datepicker/dist/react-datepicker.css";
import { getSubJobAppliedList, updateSubJobApplication } from "../../services/jobsapi";
import { useRbac } from "../../context";
import { Badge } from "../../components/ui/badge";
import { getStatusBadgeColor } from "../../components/utils/application.utils";

interface StatusHistoryItem {
  previousStatus: string;
  newStatus: string;
  changedByUserId: string;
  changedAt: string;
  rejectionReason?: string;
}

interface Application {
  _id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  jobTitle: string;
  appliedAt: string;
  status: string;
  statusHistory?: StatusHistoryItem[];
  postedByUser: string;

  // Public candidate profile fields
  fatherName?: string;
  gender?: string;
  currentLocationDetails?: string;
  dateOfBirth?: string;
  aadharNumber?: string;
  highestQualification?: string;
  spokenLanguages?: string[];
  totalExperienceYears?: number;
  totalExperienceMonths?: number;
  skills?: string[];
  preferredWorkTypes?: string[];
}

interface PaginationState {
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export function PublicSubjobApplications() {
  const navigate = useNavigate();
  const controls = useAnimation();

  // Inject custom styles for DatePicker
  useEffect(() => {
    if (typeof document !== "undefined" && datePickerStyles) {
      const styleSheet = document.createElement("style");
      styleSheet.innerText = datePickerStyles;
      document.head.appendChild(styleSheet);
      return () => {
        if (document.head.contains(styleSheet)) {
          document.head.removeChild(styleSheet);
        }
      };
    }
  }, []);



  // State management
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalResults: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedJob, setSelectedJob] = useState("all");
  const [selectedDateFrom, setSelectedDateFrom] = useState("");
  const [selectedDateTo, setSelectedDateTo] = useState("");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [jobs, setJobs] = useState<any[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  
  // Rejection modal state
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [rejectionError, setRejectionError] = useState("");

  // Application details modal state
  const [showApplicationDetailsModal, setShowApplicationDetailsModal] = useState(false);
  const [selectedApplicationForDetails, setSelectedApplicationForDetails] = useState<Application | null>(null);

  // Mock data for jobs and applications
  const jobOptions: SearchableSelectOption[] = useMemo(() => {
    const options: SearchableSelectOption[] = [{ value: "all", label: "Status" },{ value: "applied", label: "Applied" },{ value: "shortlisted", label: "Shortlisted" },{ value: "rejected", label: "Rejected" }];

    return options;
  }, [ isLoadingJobs]);

  // Mock fetch jobs
  const fetchJobs = useCallback(async () => {
    setIsLoadingJobs(true);
    try {
      // Mock job data
      const mockJobs = [
        { id: "1", title: "Software Engineer" },
        { id: "2", title: "Product Manager" },
        { id: "3", title: "Data Analyst" },
      ];
      setJobs(mockJobs);
    } catch (err: any) {
      console.error("Failed to fetch jobs:", err);
      setJobs([]);
      toast.error("Failed to load jobs for filtering");
    } finally {
      setIsLoadingJobs(false);
    }
  }, []);

  // Mock fetch applications
  const fetchApplications = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const query = {
        search:searchTerm,
        selectedDateFrom,
        selectedDateTo,
        skip:(pagination.page -1)*10,
        status:selectedJob
      }
      const subjobs = await getSubJobAppliedList(query);
      setApplications(subjobs[0].data);
      setPagination((prev) => ({
        ...prev,
        totalPages: Math.ceil(subjobs[0]?.metadata[0]?.totalResults / 10),
        totalResults: subjobs[0]?.metadata[0]?.totalResults || 0,
      }));
    } catch (err: any) {
      setError(err.message || "Failed to fetch applications");
      toast.error(err.message || "Failed to fetch applications", { duration: 4000 });
    } finally {
      setIsLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, selectedJob, selectedDateFrom, selectedDateTo, sortBy, sortOrder]);

  useEffect(() => {
    fetchApplications();
    fetchJobs();
  }, [fetchApplications, fetchJobs]);

  // Debounced search
  const debounce = useCallback((fn: (...args: any[]) => void, delay: number) => {
    let timer: NodeJS.Timeout | null = null;
    return (...args: any[]) => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fn(...args);
        timer = null;
      }, delay);
    };
  }, []);

  const debouncedSearch = useMemo(
    () => debounce((value: string) => setSearchTerm(value), 500),
    [debounce]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchTerm(value);
      debouncedSearch(value);
      fetchApplications()
    },
    [debouncedSearch]
  );

  // Date range validation
  const isValidDateRange = useCallback(() => {
    if (!selectedDateFrom || !selectedDateTo) return true;
    return new Date(selectedDateFrom) <= new Date(selectedDateTo);
  }, [selectedDateFrom, selectedDateTo]);

  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    if (!isValidDateRange()) {
      setDateError("End date cannot be before start date.");
    } else {
      setDateError(null);
    }
  }, [selectedDateFrom, selectedDateTo, isValidDateRange]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, page }));
      fetchApplications()
    }
    
  };

  // Toggle logic for Accept/Reject
const handleAccept = async (_id: string) => {
  try {
    const response = await updateSubJobApplication(_id, "shortlisted");
    console.log(response);

    setApplications((prev) =>
      prev.map((app) =>
        app._id === _id
          ? { ...app, status: "shortlisted" }
          : app
      )
    );

    toast.success(`Application ${_id} accepted!`);
  } catch (error: any) {
    // Check if it's an Axios error with a status code
    if (error.response?.status === 409) {
      toast.error("This application already exists or is duplicated.");
    } else {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  }
};


  const handleReject = (_id: string) => {
    setSelectedApplicationId(_id);
    setRejectionReason("");
    setRejectionError("");
    setShowRejectionModal(true);
  };

  const handleConfirmRejection = async () => {
    if (!rejectionReason.trim()) {
      setRejectionError("Rejection reason is required");
      return;
    }

    if (!selectedApplicationId) {
      toast.error("No application selected");
      return;
    }

    try {
      await updateSubJobApplication(selectedApplicationId, 'rejected', rejectionReason);
      
      setApplications((prev) =>
        prev.map((app) =>
          app._id === selectedApplicationId
            ? {
              ...app,
              status: "rejected"
            }
            : app
        )
      );
      
      toast.success(`Application ${selectedApplicationId} rejected successfully`);
      setShowRejectionModal(false);
      setRejectionReason("");
      setSelectedApplicationId(null);
      setRejectionError("");
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to reject application");
    }
  };

  const handleCancelRejection = () => {
    setShowRejectionModal(false);
    setRejectionReason("");
    setSelectedApplicationId(null);
    setRejectionError("");
  };

  const handleShowApplicationDetails = (app: Application) => {
    setSelectedApplicationForDetails(app);
    setShowApplicationDetailsModal(true);
  };

  const SortableHeader: React.FC<{ column: string; children: React.ReactNode; className?: string }> = ({
    column,
    children,
    className,
  }) => (
    <th
      className={cn(
        "px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors sticky top-0 bg-[#FFF5F2] z-10",
        className
      )}
      onClick={() => {
        if (sortBy === column) {
          setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
          setSortBy(column);
          setSortOrder("asc");
        }
      }}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortBy === column && (
          sortOrder === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
        )}
      </div>
    </th>
  );

  const user = useRbac()
  
  // Helper function to get rejection reason
  const getRejectionReason = (application: Application): string | null => {
    if (application.status !== 'rejected') return null;
    
    // Safety check for statusHistory
    if (!application.statusHistory || !Array.isArray(application.statusHistory)) {
      return null;
    }
    
    // Find the most recent rejection in status history
    const rejectionHistory = application.statusHistory
      .filter(history => history.newStatus === 'rejected')
      .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime());
    
    return rejectionHistory.length > 0 ? rejectionHistory[0].rejectionReason || null : null;
  };

  return (
    <div>
      <div className="bg-white border-b border-gray-200 px-3 sm:px-6 py-4 flex">
        <div>
          <Button variant="ghost" size="sm" className="p-1 sm:p-2" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </div>
        <div className="w-full">
          <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Subjob Applications</h1>
                <p className="text-xs sm:text-sm text-gray-500">Manage and track public subjob applications efficiently</p>
              </div>
            </div>
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search Applications..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-10 w-full sm:w-64 border-gray-300"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <SearchableSelect
              options={jobOptions}
              value={selectedJob}
              onValueChange={(v) => {
                setSelectedJob(v);
                fetchApplications()
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              placeholder={isLoadingJobs ? "Loading jobs..." : "All Jobs"}
              emptyText="No jobs found"
              searchPlaceholder="Search jobs..."
              className="w-37 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
              searchable
            />
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDateFrom}
                onChange={(e) => {

                  setSelectedDateFrom(e.target.value);
                  fetchApplications()
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                max={selectedDateTo || undefined}
                className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                title="From Date"
              />
              <Input
                type="date"
                value={selectedDateTo}
                onChange={(e) => {
                  setSelectedDateTo(e.target.value);
                  setPagination((p) => ({ ...p, page: 1 }));
                }}
                min={selectedDateFrom || undefined}
                className="w-full sm:w-40 h-10 rounded-lg border-gray-200 focus:ring-2 focus:ring-orange-500"
                title="To Date"
              />
            </div>
          </div>
          {dateError && <p className="text-red-500 text-sm mt-2">{dateError}</p>}
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-background relative">
        <div className="h-[calc(100vh-275px)] overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead style={{ backgroundColor: "#FFF5F2" }}>
              <tr>
                <th className="px-4 py-3 w-12 sticky top-0 bg-[#FFF5F2] z-10"></th>
                <SortableHeader column="candidate.name" className="min-w-48 sticky left-0 bg-[#FFF5F2] z-20 border-r border-gray-200">
                  Name
                </SortableHeader>
                <SortableHeader column="companyName" className="bg-[#FFF5F2] min-w-48">Company Name</SortableHeader>
                <SortableHeader column="jobTitle" className="bg-[#FFF5F2] min-w-48">Job Title</SortableHeader>
                <SortableHeader column="candidate.phone" className="bg-[#FFF5F2] min-w-32">Phone</SortableHeader>
                <SortableHeader column="candidate.email" className="bg-[#FFF5F2] min-w-64">Email</SortableHeader>
                <SortableHeader column="appliedAt" className="bg-[#FFF5F2] min-w-40">Applied At</SortableHeader>
                <SortableHeader column="appliedAt" className="bg-[#FFF5F2] min-w-40">Uploaded by</SortableHeader>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-32 sticky top-0 bg-[#FFF5F2] z-10">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-gray-600">
                      <svg
                        className="animate-spin h-5 w-5 text-orange-500"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                        ></path>
                      </svg>
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">{error}</p>
                  </td>
                </tr>
              ) : applications.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center">
                    <p className="text-gray-500">No applications found matching your criteria.</p>
                  </td>
                </tr>
              ) : (
                applications.map((app) => (
                  <tr
                    key={app._id}
                    className={cn("hover:bg-gray-50 transition-colors cursor-pointer")}
                    onClick={() => handleShowApplicationDetails(app)}
                  >
                    <td className="px-4 py-4"></td>
                    <td className="px-4 py-4 sticky left-0 bg-white z-10 border-r border-gray-200">
                      <span className="font-medium text-gray-900">{app.name}</span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">{app.company}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">{app.jobTitle}</td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy phone number"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.phone);
                          toast.success("Phone number copied!");
                        }}
                      >
                        {app.phone}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-900">
                      <span
                        className="cursor-pointer hover:text-orange-600"
                        title="Copy email"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(app.email);
                          toast.success("Email copied!");
                        }}
                      >
                        {app.email}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {app.postedByUser}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex gap-2">
                        {app.status === "applied" ? user.role === "ADMIN" ? <Badge className={cn(getStatusBadgeColor(app.status),"pointer-events-none")}>applied</Badge> :<><Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-md border border-orange-300 px-3 py-1 text-sm font-medium text-orange-500 bg-white hover:bg-orange-50"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccept(app._id);
                        }}
                      >
                        Shortlist
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className={cn(
                          "rounded-md border border-orange-300 px-3 py-1 text-sm font-medium text-orange-500 bg-white hover:bg-orange-50"
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleReject(app._id);
                        }}
                      >
                        Reject
                      </Button></> : <>{app.status === "shortlisted" ? <Badge className={cn(getStatusBadgeColor("shortlisted"),"pointer-events-none")}>Shortlisted</Badge> : 
                       
                          <Badge 
                            className={cn(getStatusBadgeColor("rejected"),"pointer-events-none")}
                          >
                            Rejected
                          </Badge>
                        
                      }</>}
                     
                    </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between gap-2 p-4 pl-8">
          <div className="text-sm text-gray-500">
            Showing {applications.length} of {pagination.totalResults} applications
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1 || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter(
                (pageNum) =>
                  pageNum === 1 ||
                  pageNum === pagination.totalPages ||
                  (pageNum >= pagination.page - 2 && pageNum <= pagination.page + 2)
              )
              .map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pagination.page === pageNum ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePageChange(pageNum)}
                  disabled={isLoading || pagination.page == pageNum}
                  className={
                    pagination.page === pageNum
                      ? "bg-orange-600 text-white hover:bg-orange-700"
                      : "border-gray-200 hover:bg-orange-50"
                  }
                >
                  {pageNum}
                </Button>
              ))}
            {pagination.totalPages > 5 && pagination.page < pagination.totalPages - 2 && (
              <span className="text-sm text-gray-500">...</span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages || isLoading}
              className="rounded-lg border-gray-200 hover:bg-orange-50"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Rejection Reason Modal */}
      <Dialog open={showRejectionModal} onOpenChange={setShowRejectionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold text-gray-900">
              Reject Application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-reason" className="text-sm font-medium text-gray-700">
                Rejection Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="rejection-reason"
                placeholder="Please provide a reason for rejecting this application..."
                value={rejectionReason}
                onChange={(e) => {
                  setRejectionReason(e.target.value);
                  setRejectionError("");
                }}
                className={`min-h-[100px] resize-none ${
                  rejectionError ? "border-red-500 focus:border-red-500" : ""
                }`}
              />
              {rejectionError && (
                <p className="text-sm text-red-500">{rejectionError}</p>
              )}
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleCancelRejection}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRejection}
              disabled={!rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reject Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Application Details Modal */}
      <Dialog
        open={showApplicationDetailsModal}
        onOpenChange={(open) => {
          setShowApplicationDetailsModal(open);
          if (!open) setSelectedApplicationForDetails(null);
        }}
      >
        <DialogContent className="w-[95vw] max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-4xl xl:max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl sm:rounded-3xl no-scrollbar">
          <DialogHeader className="pb-4">
            <DialogTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="h-10 w-10 sm:h-12 sm:w-12 bg-orange-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">
                {(selectedApplicationForDetails?.name || "A").charAt(0).toUpperCase()}
              </div>
              <span className="break-words">{selectedApplicationForDetails?.name}</span>
            </DialogTitle>
          </DialogHeader>

          {selectedApplicationForDetails && (
            <div className="space-y-4 sm:space-y-6 p-0">
              {/* Basic Information */}
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Email</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                        {selectedApplicationForDetails.email || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Phone</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.phone || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Father's Name</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.fatherName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Gender</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.gender || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Location</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base break-words">
                        {selectedApplicationForDetails.currentLocationDetails || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Date of Birth</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.dateOfBirth
                          ? new Date(selectedApplicationForDetails.dateOfBirth).toLocaleDateString("en-GB")
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Aadhar Number</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.aadharNumber || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Highest Qualification</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.highestQualification || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Spoken Languages</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {Array.isArray(selectedApplicationForDetails.spokenLanguages) &&
                        selectedApplicationForDetails.spokenLanguages.length > 0
                          ? selectedApplicationForDetails.spokenLanguages.join(", ")
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Experience</p>
                      <p className="font-medium text-gray-900 text-sm sm:text-base">
                        {selectedApplicationForDetails.totalExperienceYears !== undefined &&
                        selectedApplicationForDetails.totalExperienceMonths !== undefined
                          ? `${selectedApplicationForDetails.totalExperienceYears} years ${selectedApplicationForDetails.totalExperienceMonths} months`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Skills</p>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {Array.isArray(selectedApplicationForDetails.skills) &&
                        selectedApplicationForDetails.skills.length > 0 ? (
                          selectedApplicationForDetails.skills.slice(0, 6).map((skill, idx) => (
                            <Badge key={idx} variant="secondary" className="text-xs">
                              {skill}
                            </Badge>
                          ))
                        ) : (
                          <span className="font-medium text-gray-900 text-sm sm:text-base">N/A</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Professional Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Work Mode</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">
                      {Array.isArray(selectedApplicationForDetails.preferredWorkTypes) &&
                      selectedApplicationForDetails.preferredWorkTypes.length > 0
                        ? selectedApplicationForDetails.preferredWorkTypes.join(", ")
                        : "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Job Category</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">N/A</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Employment Type</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">N/A</p>
                  </div>
                </div>
              </div>

              {/* Application Status */}
              <div className="bg-gradient-to-r from-orange-50 to-indigo-50 rounded-xl p-4 sm:p-6">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">
                  Application Status
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Current Status</p>
                    <Badge className={cn(getStatusBadgeColor(selectedApplicationForDetails.status), "pointer-events-none")}>
                      {selectedApplicationForDetails.status || "N/A"}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Expected Joining Date</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">N/A</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm text-gray-600">Applied At</p>
                    <p className="font-medium text-gray-900 text-sm sm:text-base">
                      {selectedApplicationForDetails.appliedAt
                        ? new Date(selectedApplicationForDetails.appliedAt).toLocaleDateString("en-GB")
                        : "N/A"}
                    </p>
                  </div>
                </div>

                {selectedApplicationForDetails.status === "rejected" && getRejectionReason(selectedApplicationForDetails) && (
                  <div className="mt-4 bg-red-50 border border-red-100 rounded-lg p-3">
                    <p className="text-xs text-red-700 font-medium">Rejection Reason</p>
                    <p className="text-sm text-gray-900 mt-1">{getRejectionReason(selectedApplicationForDetails)}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApplicationDetailsModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ViewApplications({ onAddCandidate }: { onAddCandidate?: () => void }) {
  return <PublicSubjobApplications />;
}